import { DETAIL_VUE, LIBELLE_VUE, TRI_PAR_DEFAUT_VUE, VUES, type Vue } from '@mosaik/core';
import { useCommandes, type FiltresCommandes } from '../api/hooks';
import type {
  ActionCalculee, CommandeListee, DocumentReglages, EtatIntegration, ResolutionHubspot,
} from '../api/types';
import { CaseAvis } from '../composants/CaseAvis';
import { Chargement, Erreur, Vide } from '../composants/Etats';
import { LienBoutique, LienClient } from '../composants/Liens';
import { Litiges } from '../composants/Litiges';
import { Pagination } from '../composants/Pagination';
import { Progression } from '../composants/Progression';
import { libelleAction, useActionCommande, type ModalesCommande } from '../composants/actions-commande';
import { euros, formatCourt, libelleFournisseurs } from '../format';

/**
 * Les onglets ne découpent plus par étape mais par question posée : qu'est-ce
 * qui me réclame maintenant, qu'est-ce qui tourne, qu'est-ce qui a déjà
 * déraillé, qu'est-ce qui est fini. « Toutes » reste en dernier, comme filet.
 */
const ONGLETS: Array<[Vue, string]> = VUES.map((v) => [v, LIBELLE_VUE[v]]);


export function PageCommandes({
  filtres, setFiltres, selection, onSelection, reglages, hubspot, onDoublons, onLienBloque,
  modales,
}: {
  filtres: FiltresCommandes;
  setFiltres: (f: Partial<FiltresCommandes>) => void;
  selection: string | null;
  onSelection: (id: string) => void;
  reglages: DocumentReglages | undefined;
  hubspot: EtatIntegration | undefined;
  onDoublons: (r: Extract<ResolutionHubspot, { statut: 'doublons' }>) => void;
  onLienBloque: (url: string, client: string) => void;
  modales: ModalesCommande;
}) {
  const { data, isLoading, isFetching, error, refetch } = useCommandes(filtres);
  // Un seul exécuteur pour toute la page : deux actions ne peuvent pas partir en
  // même temps, ce qui est exactement ce qu'on veut sur une liste cliquable.
  const { executer, enAttente } = useActionCommande(reglages, modales);

  const vue = (filtres.vue ?? 'encours') as Vue;
  /**
   * « À faire » se passe de la colonne Fournisseur.
   *
   * L'action y nomme déjà le fournisseur — « Envoyer à Ceramica », « Relancer
   * Ceramica » : la colonne répétait le même mot deux cellules plus loin. Elle
   * reste dans les autres onglets, où aucune action ne le dit.
   */
  const avecFournisseur = vue !== 'afaire';

  /*
   * « En cours » ne propose AUCUN geste, et l'en-tête le dit.
   *
   * L'onglet contient ce qui suit son cours tout seul — mais aussi les
   * commandes dont l'action est BLOQUÉE par un règlement qui n'est pas arrivé.
   * Celles-là affichaient leur bouton, alors que l'onglet tout entier existe
   * pour dire « rien à faire ici aujourd'hui ». Proposer un geste dans la
   * colonne d'un onglet qui n'en réclame aucun brouillait les deux.
   *
   * La colonne y devient donc « Statut » : ce qu'on attend, et ce qui retient
   * la commande quand quelque chose la retient.
   *
   * L'archive non plus : une commande close n'appelle plus de geste, et un
   * bouton y laissait croire le contraire.
   */
  const avecAction = vue !== 'encours' && vue !== 'archive';

  return (
    <section className="page" id="contenu">
      <div className="filters">
        <div className="seg" role="group" aria-label="Filtrer la liste">
          {ONGLETS.map(([cle, libelle]) => (
            <button
              key={cle} type="button"
              aria-pressed={vue === cle}
              title={DETAIL_VUE[cle]}
              // Chaque onglet a le tri qui lui va : les urgences par gravité, le
              // reste par date. C'est ce qui permet de se passer d'un menu de
              // tri — il n'y avait qu'un bon choix par onglet.
              onClick={() => setFiltres({ vue: cle, tri: TRI_PAR_DEFAUT_VUE[cle], page: 1 })}
            >
              {libelle}{' '}
              <span style={{ opacity: 0.55 }}>{data?.compteurs?.[cle] ?? '—'}</span>
            </button>
          ))}
        </div>

        {isFetching && !isLoading && <span className="spin" aria-label="Actualisation en cours" />}
      </div>

      {error && <Erreur erreur={error} onReessayer={() => void refetch()} />}
      {isLoading && !data && <Chargement libelle="Chargement des commandes…" />}

      {data && data.elements.length === 0 && !error && (
        <Vide
          titre="Aucune commande"
          aide={
            filtres.q || (filtres.etape && filtres.etape !== 'tous') || (filtres.action && filtres.action !== 'tous')
              ? 'Élargissez les filtres ou videz la recherche.'
              : "La base ne contient encore aucune commande. Lancez l'import, ou `pnpm seed --demo` pour un jeu d'essai."
          }
        />
      )}

      {data && data.elements.length > 0 && (
        <>
          <div className="tablewrap">
            <table>
              <caption className="sr-only" style={{ position: 'absolute', left: -9999 }}>
                Liste des commandes, {data.total} au total
              </caption>
              <thead>
                <tr>
                  <th scope="col">Commande</th>
                  <th scope="col">Client</th>
                  {avecFournisseur && <th scope="col">Fournisseur</th>}
                  <th scope="col" style={{ width: 170 }}>Progression</th>
                  <th scope="col">Litige</th>
                  <th scope="col">
                    {vue === 'archive' ? 'Avis' : avecAction ? 'À faire' : 'Statut'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.elements.map((c) => (
                  <Ligne
                    key={c.id} commande={c} selectionnee={selection === c.id}
                    avecFournisseur={avecFournisseur} avecAction={avecAction} reglages={reglages}
                    archive={vue === 'archive'}
                    onOuvrir={() => onSelection(c.id)} hubspot={hubspot}
                    onDoublons={onDoublons} onLienBloque={onLienBloque}
                    onAction={(action) => void executer(c, action)}
                    enAttente={enAttente}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={data.page} pages={data.pages} total={data.total} taille={data.taille}
            onPage={(p) => setFiltres({ page: p })}
          />
        </>
      )}
    </section>
  );
}

function Ligne({
  commande, selectionnee, avecFournisseur, avecAction, reglages, onOuvrir, hubspot, onDoublons,
  onLienBloque, onAction, enAttente, archive,
}: {
  commande: CommandeListee;
  selectionnee: boolean;
  /** Sert au libellé du bouton d'action : qui l'on relance dépend de l'annuaire. */
  reglages: DocumentReglages | undefined;
  /** `false` dans « À faire », où l'action nomme déjà le fournisseur. */
  avecFournisseur: boolean;
  /** `false` dans « En cours », qui informe au lieu de proposer un geste. */
  avecAction: boolean;
  /** Dans l'Archive, la dernière colonne porte la case d'avis, et rien d'autre. */
  archive: boolean;
  onOuvrir: () => void;
  hubspot: EtatIntegration | undefined;
  onDoublons: (r: Extract<ResolutionHubspot, { statut: 'doublons' }>) => void;
  onLienBloque: (url: string, client: string) => void;
  onAction: (action: ActionCalculee) => void;
  enAttente: boolean;
}) {
  const { calcul } = commande;
  const action = calcul.actions[0];
  return (
    <tr
      // Plus de classe de gravité : la ligne ne se teinte plus. Ce qu'il y a à
      // faire se lit dans la colonne « Action », en toutes lettres.
      className={`cliquable${selectionnee ? ' sel' : ''}`}
      onClick={onOuvrir}
      // La référence étant devenue un lien externe, c'est la ligne qui doit
      // porter l'ouverture de la fiche au clavier. Sans lien, le bouton de la
      // première cellule s'en charge déjà : pas de second arrêt de tabulation.
      tabIndex={commande.liens.boutique ? 0 : undefined}
      aria-label={commande.liens.boutique ? `Ouvrir la commande ${commande.ref}` : undefined}
      onKeyDown={(e) => {
        if (!commande.liens.boutique || e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOuvrir(); }
      }}
    >
      <td className="lead">
        {/* Avec un lien vers la boutique, la référence lui appartient : c'est la
            ligne qui ouvre la fiche, au clic comme au clavier. Sans lien, le
            bouton d'origine reste, et porte cette accessibilité. */}
        {commande.liens.boutique ? (
          <LienBoutique commande={commande}>{commande.ref}</LienBoutique>
        ) : (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOuvrir(); }}
            className="mono"
            style={{ background: 'none', border: 0, padding: 0, fontWeight: 600, textAlign: 'left' }}
            aria-label={`Ouvrir la commande ${commande.ref}`}
          >
            {commande.ref}
          </button>
        )}
        <div className="sub">{formatCourt(commande.dateReception)} · {euros(commande.montant)}</div>
      </td>

      <td>
        <LienClient
          commande={commande} hubspot={hubspot}
          onDoublons={onDoublons} onLienBloque={onLienBloque}
        />
        <div className="sub">{commande.ville}</div>
      </td>

      {avecFournisseur && (
        <td>
          {commande.fournisseurs.length
            ? <span className="tag">{libelleFournisseurs(commande.fournisseurs)}</span>
            : <span className="sub" style={{ margin: 0 }}>non renseigné</span>}
        </td>
      )}

      <td className="tcell">
        <Progression calcul={calcul} compact />
        {/* Plusieurs colis : la liste montre celui qui va le plus mal — bloqué,
            ou muet depuis le plus longtemps. Le même que suit le moteur. */}
        {commande.suiviAffiche && commande.suiviAffiche.nombreColis > 1 && !commande.livre && (
          <div className="sub" title={`${commande.suiviAffiche.nombreColis} colis sur cette commande`}>
            <span className="mono">{commande.suiviAffiche.tracking}</span>
            {' : '}{commande.suiviAffiche.statut || 'aucun point de suivi'}
            {' · '}{commande.suiviAffiche.nombreColis} colis
          </div>
        )}
      </td>

      {/* Entre ce qui s'est passé et ce qu'il reste à faire : la colonne se lit
          dans l'ordre du récit. */}
      <td><Litiges litiges={calcul.litiges} /></td>

      <td>
        {/* Cette colonne ne porte QUE du travail dû, et seulement là où il y
            en a. « Remettre en cours » y a figuré et n'y avait pas sa place :
            défaire une clôture n'est pas une action due, c'est la réparation
            d'un geste précédent, et elle se prend dans la fiche. Une liste
            dense n'est pas l'endroit où l'on rouvre une commande. */}
        {archive ? (
          /* L'Archive ne dit pas « livrée » : toutes le sont, c'est ce qui les
             y range. Ce qui reste à lire d'une commande close, c'est l'avis —
             programmé, parti, ou encore à cocher. La case est la même que dans
             « À faire » : cochée d'office quand la livraison a été rapide. */
          <CaseAvis commande={commande} enAttente={enAttente} compact />
        ) : !avecAction ? (
          /* « En cours » : on dit ce qu'on attend, et ce qui retient la
             commande. Jamais de bouton — l'onglet ne réclame rien, et c'est
             tout son sens. Le geste reste possible, depuis la fiche. */
          <>
            {/* Du texte, sans étiquette ni fond.

                L'attente s'affichait dans une pastille verte — `tag pine` —,
                héritée du temps où elle comblait une case vide dans un tableau
                d'actions. Ici elle est le contenu même de la colonne : la
                colorer revenait à surligner chaque ligne de l'onglet, ce qui ne
                distingue plus rien et donne un vert de réussite à des commandes
                qui ne font qu'attendre.

                Le motif du report suit, en gris discret : c'est une
                information, pas une alerte. */}
            <div>{calcul.attente}</div>
          </>
        ) : action?.cle === 'avis' ? (
          /* La demande d'avis ne se fait pas d'un clic : elle se PROGRAMME, et
             une case dit cela mieux qu'un bouton — un état qu'on pose et qu'on
             peut défaire, pas un ordre qu'on lance. Même composant que la
             fiche : un seul comportement à vérifier. */
          <CaseAvis commande={commande} enAttente={enAttente} compact />
        ) : action ? (
          <>
            {/* L'action se fait ici : cliquer revient à ouvrir la fiche et à
                cliquer son bouton, sans le détour. La propagation est coupée,
                sinon la ligne ouvrirait la fiche par-dessus.

                Il se lit comme un BOUTON, et non comme une étiquette : c'est
                le seul endroit de la ligne où cliquer agit au lieu d'ouvrir la
                fiche, et rien ne le disait.

                Sans code couleur pour autant : dans un onglet qui ne contient
                QUE des choses à faire, colorer par gravité ne hiérarchise plus
                rien — le tri s'en charge déjà — et ne laisse qu'un tableau
                bariolé. */}
            <button
              type="button"
              className={`btn sm act${action.blocage ? ' bloquee' : ''}`}
              disabled={enAttente}
              style={{ cursor: enAttente ? 'progress' : 'pointer' }}
              title={[
                action.detail,
                action.blocage,
                `→ ${libelleAction(action, commande, reglages)}`,
              ].filter(Boolean).join('\n\n')}
              onClick={(e) => { e.stopPropagation(); onAction(action); }}
            >
              {action.titre}
            </button>
            {/* La facture non réglée se lit AVANT le clic : la confirmation
                qui suit ne doit pas être la première nouvelle. */}
            {action.blocage && (
              <div className="sub" style={{ margin: '4px 0 0', color: 'var(--brick)' }}>
                Facture non réglée
              </div>
            )}
            {calcul.actions.length > 1 && <div className="sub">+{calcul.actions.length - 1} autre</div>}
          </>
        ) : commande.avis.demande ? (
          /* Plus d'action due, mais l'avis est programmé : la date se lit ici,
             sans quoi cocher ferait disparaître ce qu'on vient de poser. */
          <CaseAvis commande={commande} enAttente={enAttente} compact />
        ) : (
          /* Jamais de case vide : à défaut d'action, on dit ce qu'on attend. */
          <span className="tag pine">{calcul.attente}</span>
        )}
      </td>
    </tr>
  );
}
